/**
 * Names, floating over heads.
 *
 * Two things at once, because they are the same thing: who that person over
 * there is, and what the animal under your crosshair is called. A world with
 * other people in it and no names is a world of identical strangers, and a
 * game that teaches you the word "тур" only in the storeroom teaches it
 * nowhere useful.
 *
 * Elements rather than sprites in the scene. Text drawn into the world wants
 * a texture per name, a font atlas and a rule about what happens when a name
 * is behind a wall; a `div` at a projected point wants none of that, and
 * reads at any distance because it is not being scaled by perspective.
 *
 * The pool is reused across frames. Building and dropping a dozen elements
 * sixty times a second is how a page comes to stutter every few seconds for
 * no reason anybody can see.
 */

export interface NameTag {
  readonly text: string;
  /** Where in the world the name hangs - usually just above a head. */
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** The one under the crosshair, which is drawn differently. */
  readonly aimed?: boolean;
}

/** Where a point in the world landed on screen. */
export interface Projected {
  readonly x: number;
  readonly y: number;
  readonly visible: boolean;
}

export class NameTags {
  private readonly pool: HTMLElement[] = [];

  constructor(private readonly root: HTMLElement) {}

  /**
   * Put these names on screen and no others.
   *
   * Anything left over from last frame is hidden rather than removed, so the
   * next frame that needs it can have it back.
   */
  show(
    tags: readonly NameTag[],
    project: (x: number, y: number, z: number) => Projected,
  ): void {
    for (let i = 0; i < tags.length; i++) {
      const tag = tags[i]!;
      const at = project(tag.x, tag.y, tag.z);
      const el = this.at(i);
      // Behind the camera projects to a point in front of it, which would put
      // a name over somebody standing at your back.
      if (!at.visible) { el.hidden = true; continue; }
      el.hidden = false;
      el.className = tag.aimed === true ? "name-tag aimed" : "name-tag";
      el.textContent = tag.text;
      el.style.left = `${Math.round(at.x)}px`;
      el.style.top = `${Math.round(at.y)}px`;
    }
    for (let i = tags.length; i < this.pool.length; i++) this.pool[i]!.hidden = true;
  }

  private at(i: number): HTMLElement {
    const found = this.pool[i];
    if (found !== undefined) return found;
    const el = document.createElement("div");
    el.className = "name-tag";
    el.hidden = true;
    this.root.append(el);
    this.pool[i] = el;
    return el;
  }
}
